import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import { getStores, getMenu } from '@/services/api';
import apiClient from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Store, Menu, Users, Settings, LogOut, Bike, Lock } from 'lucide-react';
import { toast } from 'sonner';
import MenuManagement from './MenuManagement';
import RiderManagement from './RiderManagement';
import UserManagement from './UserManagement';

const AdminDashboard = () => {
  const [user, setUser] = useState(null);
  const [stores, setStores] = useState([]);
  const [menuStats, setMenuStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [userCount, setUserCount] = useState(0);
  const [riderCount, setRiderCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Set page title
    document.title = 'SSPICE Admin Dashboard';
    
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (!userData.id) {
      navigate('/login');
      return;
    }
    setUser(userData);
    loadStores();
    loadMenuStats(userData.store_id);
    loadUserStats(userData.store_id);
    loadRiderStats(userData.store_id);
  }, []);

  const loadStores = async () => {
    try {
      const response = await getStores();
      setStores(response.data);
    } catch (error) {
      console.error('Error loading stores:', error);
      toast.error('Failed to load stores');
    }
  };

  const loadMenuStats = async (storeId) => {
    if (!storeId) return;
    try {
      // Get all items including inactive
      const response = await apiClient.get(`/menu?store_id=${storeId}&active_only=false`);
      const items = response.data || [];
      setMenuStats({
        total: items.length,
        active: items.filter(item => item.active !== false).length,
        inactive: items.filter(item => item.active === false).length
      });
    } catch (error) {
      console.error('Error loading menu stats:', error);
    }
  };

  const loadUserStats = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await apiClient.get(`/auth/store-users?store_id=${storeId}`);
      const users = response.data || [];
      setUserCount(users.length);
    } catch (error) {
      console.error('Error loading user stats:', error);
    }
  };

  const loadRiderStats = async (storeId) => {
    if (!storeId) return;
    try {
      const response = await apiClient.get(`/auth/riders?store_id=${storeId}`);
      const riders = response.data || [];
      setRiderCount(riders.length);
    } catch (error) {
      console.error('Error loading rider stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Store className="w-8 h-8 text-orange-600 mr-3" />
              <div className="flex items-center gap-2">
                <span className="text-xl font-bold text-gray-800">SSPICE Admin</span>
                <span className="text-xs text-red-600 font-medium">(Designed by Prashant B)</span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">Welcome, {user?.name}</span>
              <Button data-testid="change-password-button" variant="outline" size="sm" onClick={() => navigate('/change-password')}>
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </Button>
              <Button data-testid="logout-button" variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Dashboard</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card data-testid="stores-card" className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Store className="w-5 h-5 mr-2 text-orange-600" />
                Stores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-800">{stores.length}</p>
              <p className="text-sm text-gray-600 mt-2">Active stores</p>
            </CardContent>
          </Card>
          
          <Card data-testid="menu-card" className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Menu className="w-5 h-5 mr-2 text-orange-600" />
                Menu Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-800">{menuStats.total}</p>
              <p className="text-sm text-gray-600 mt-2">
                {menuStats.active} active, {menuStats.inactive} inactive
              </p>
            </CardContent>
          </Card>

          <Card data-testid="riders-card" className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Bike className="w-5 h-5 mr-2 text-orange-600" />
                Riders
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-800">{riderCount}</p>
              <p className="text-sm text-gray-600 mt-2">
                {riderCount === 0 ? 'Create riders in Riders tab' : 'Active delivery riders'}
              </p>
            </CardContent>
          </Card>

          <Card data-testid="users-card" className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center">
                <Users className="w-5 h-5 mr-2 text-orange-600" />
                Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-gray-800">{userCount}</p>
              <p className="text-sm text-gray-600 mt-2">
                Total store users (Admin, POS, Kitchen{riderCount > 0 ? ', Riders' : ''})
              </p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="stores" className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-4 gap-1">
            <TabsTrigger value="stores" className="text-xs sm:text-sm">
              <Store className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Stores</span>
            </TabsTrigger>
            <TabsTrigger value="menu" className="text-xs sm:text-sm">
              <Menu className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Menu</span>
            </TabsTrigger>
            <TabsTrigger value="riders" className="text-xs sm:text-sm">
              <Bike className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Riders</span>
            </TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm">
              <Users className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stores">
            <Card data-testid="stores-list-card">
              <CardHeader>
                <CardTitle>Stores</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {stores.map((store) => (
                    <div key={store.id} data-testid={`store-${store.code}`} className="p-4 border rounded-lg hover:bg-gray-50">
                      <h3 className="font-semibold text-lg">{store.name}</h3>
                      <p className="text-sm text-gray-600">{store.address}</p>
                      <p className="text-xs text-gray-500 mt-1">Code: {store.code}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="menu">
            <MenuManagement storeId={user?.store_id} />
          </TabsContent>

          <TabsContent value="riders">
            <RiderManagement storeId={user?.store_id} />
          </TabsContent>

          <TabsContent value="users">
            <UserManagement storeId={user?.store_id} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;